import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  VStack,
  HStack,
  Badge,
  Icon,
  Flex,
  Spacer,
  Divider,
  Portal,
} from '@chakra-ui/react';
import { ChevronDownIcon, SearchIcon, CloseIcon } from '@chakra-ui/icons';

/**
 * CheckboxSelect component - A dropdown with checkbox functionality
 * Similar to student selection but for general use
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Label for the field
 * @param {string} props.name - Name attribute
 * @param {Array} props.options - Array of options {label, value}
 * @param {Array|string} props.value - Selected values (array for multi, string for single)
 * @param {Function} props.onChange - Change handler
 * @param {boolean} props.isMulti - Enable multiple selection
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.displayFormat - How to display selections ("labels" or "count")
 * @param {boolean} props.isRequired - Whether field is required
 * @param {boolean} props.isDisabled - Whether field is disabled
 * @param {string} props.errorMessage - Error message to display
 * @param {string} props.helperText - Helper text
 * @param {string} props.size - Component size
 * @param {boolean} props.isSearchable - Enable search functionality
 * @param {number} props.maxDisplayItems - Max items to show in display before showing count
 */
const CheckboxSelect = ({
  label,
  name,
  options = [],
  value = [],
  onChange,
  isMulti = false,
  placeholder = 'Pilih opsi...',
  displayFormat = 'labels', // "labels" or "count"
  isRequired = false,
  isDisabled = false,
  errorMessage = '',
  helperText = '',
  size = 'md',
  isSearchable = true,
  maxDisplayItems = 2,
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  // Normalize value to array for consistent handling
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  // Filter options based on search query
  const filteredOptions = options.filter((option) => {
    return option?.label?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Get selected options for display
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value),
  );

  // Handle option selection
  const handleOptionChange = (optionValue, isChecked) => {
    let newValues;

    if (isMulti) {
      if (isChecked) {
        newValues = [...selectedValues, optionValue];
      } else {
        newValues = selectedValues.filter((v) => v !== optionValue);
      }
    } else {
      newValues = isChecked ? optionValue : '';
    }

    onChange && onChange(newValues);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (isMulti) {
      console.log('options', options);
      const allFilteredValues = filteredOptions.map((option) => option.value);
      // Combine existing selected values with new filtered values
      const combinedValues = [
        ...new Set([...selectedValues, ...allFilteredValues]),
      ];
      onChange && onChange(combinedValues);
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onChange && onChange(isMulti ? [] : '');
  };

  // Handle dropdown toggle with proper event handling
  const handleToggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prevIsOpen) => !prevIsOpen);
    
    // Clear search when closing
    if (isOpen) {
      setSearchQuery('');
    }
  };

  // Generate display text for selected values
  const getDisplayText = () => {
    if (selectedOptions.length === 0) {
      return placeholder;
    }

    if (displayFormat === 'count' && selectedOptions.length > 1) {
      return `${selectedOptions.length} item${
        selectedOptions.length > 1 ? 's' : ''
      } dipilih`;
    }

    if (displayFormat === 'labels') {
      if (selectedOptions.length <= maxDisplayItems) {
        return selectedOptions.map((option) => option.label).join(', ');
      } else {
        const displayItems = selectedOptions.slice(0, maxDisplayItems);
        const remainingCount = selectedOptions.length - maxDisplayItems;
        return `${displayItems
          .map((option) => option.label)
          .join(', ')} +${remainingCount} lainnya`;
      }
    }

    return selectedOptions.map((option) => option.label).join(', ');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the menu content or the button
      if (
        (menuRef.current && menuRef.current.contains(event.target)) ||
        (buttonRef.current && buttonRef.current.contains(event.target))
      ) {
        return;
      }
      
      // Close the dropdown if clicking outside
      if (isOpen) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]); // Add isOpen dependency to ensure we have the latest state

  // Size configurations
  const sizeConfig = {
    sm: {
      button: { h: 8, fontSize: 'sm' },
      menu: { maxH: '200px' },
      text: { fontSize: 'sm' },
    },
    md: {
      button: { h: 10, fontSize: 'md' },
      menu: { maxH: '250px' },
      text: { fontSize: 'md' },
    },
    lg: {
      button: { h: 12, fontSize: 'lg' },
      menu: { maxH: '300px' },
      text: { fontSize: 'lg' },
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  return (
    <FormControl
      isInvalid={!!errorMessage}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      {label && (
        <FormLabel htmlFor={name} fontSize={config.text.fontSize}>
          {label}
        </FormLabel>
      )}

      <Menu
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSearchQuery('');
        }}
        closeOnSelect={false}
        placement="bottom-start"
      >
        <MenuButton
          ref={buttonRef}
          as={Button}
          rightIcon={<ChevronDownIcon />}
          textAlign="left"
          fontWeight="normal"
          variant="outline"
          bg="white"
          borderColor={errorMessage ? 'red.500' : 'gray.200'}
          _hover={{
            borderColor: errorMessage ? 'red.500' : 'gray.300',
          }}
          _focus={{
            borderColor: errorMessage ? 'red.500' : 'blue.500',
            boxShadow: errorMessage
              ? '0 0 0 1px red.500'
              : '0 0 0 1px blue.500',
          }}
          _active={{
            borderColor: errorMessage ? 'red.500' : 'blue.500',
          }}
          onClick={handleToggleDropdown}
          disabled={isDisabled}
          w="full"
          justifyContent="space-between"
          {...config.button}
          {...props}
        >
          <Text
            color={selectedOptions.length === 0 ? 'gray.400' : 'gray.700'}
            fontSize={config.text.fontSize}
          >
            {getDisplayText()}
          </Text>
        </MenuButton>

        <Portal>
          <MenuList
            ref={menuRef}
            w="full"
            maxH={config.menu.maxH}
            overflowY="auto"
            zIndex={1400}
            p={0}
            boxShadow="0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            border="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
          {/* Search Input */}
          {isSearchable && (
            <Box p={2} borderBottomWidth={1} borderColor="gray.100">
              <InputGroup size="sm">
                <InputLeftElement>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  placeholder="Cari..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="filled"
                  size="sm"
                />
                {searchQuery && (
                  <InputRightElement>
                    <Icon
                      as={CloseIcon}
                      w={3}
                      h={3}
                      color="gray.400"
                      cursor="pointer"
                      onClick={() => setSearchQuery('')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          )}

          {/* Bulk Actions */}
          {isMulti && filteredOptions.length > 0 && (
            <Box p={2} borderBottomWidth={1} borderColor="gray.100">
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleSelectAll}
                  isDisabled={filteredOptions.length === selectedValues.length}
                >
                  Pilih Semua
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleClearAll}
                  isDisabled={selectedValues.length === 0}
                >
                  Hapus Semua
                </Button>
                <Spacer />
                <Badge colorScheme="blue" variant="subtle">
                  {selectedValues.length} dipilih
                </Badge>
              </HStack>
            </Box>
          )}

          {/* Options List */}
          <VStack spacing={0} align="stretch" maxH="200px" overflowY="auto">
            {filteredOptions.length === 0 ? (
              <MenuItem isDisabled>
                <Text color="gray.500" fontSize="sm">
                  {searchQuery ? 'Tidak ditemukan' : 'Tidak ada opsi'}
                </Text>
              </MenuItem>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);

                return (
                  <MenuItem
                    key={option.value}
                    onClick={(e) => {
                      // Let checkbox handle its own clicks
                      if (
                        e.target.type === 'checkbox' ||
                        e.target.closest('label')
                      ) {
                        return;
                      }
                      handleOptionChange(option.value, !isSelected);
                    }}
                    bg={isSelected ? 'blue.50' : 'white'}
                    _hover={{
                      bg: isSelected ? 'blue.100' : 'gray.50',
                    }}
                    _focus={{
                      bg: isSelected ? 'blue.100' : 'gray.50',
                    }}
                  >
                    <HStack w="full" spacing={3}>
                      <Checkbox
                        isChecked={isSelected}
                        onChange={() => {
                          handleOptionChange(option.value, !isSelected);
                        }}
                        colorScheme="blue"
                        size="md"
                      />
                      <Text flex={1} fontSize="sm">
                        {option.label}
                      </Text>
                    </HStack>
                  </MenuItem>
                );
              })
            )}
          </VStack>
          </MenuList>
        </Portal>
      </Menu>

      {helperText && !errorMessage && (
        <FormHelperText fontSize="sm" color="gray.600">
          {helperText}
        </FormHelperText>
      )}

      {errorMessage && (
        <FormErrorMessage fontSize="sm">{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export default CheckboxSelect;
